import React, { useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import s from './WordEditor.module.css';

interface WordEditorProps {
    content: string;
    onChange: (content: string) => void;
    placeholder?: string;
    onImageUpload?: (file: File) => Promise<string>;
}

export const WordEditor: React.FC<WordEditorProps> = ({
                                                          content,
                                                          onChange,
                                                          placeholder = 'Начните писать...',
                                                          onImageUpload
                                                      }) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3, 4],
                },
            }),
            Image.configure({
                inline: false,
                allowBase64: true,
                HTMLAttributes: {
                    class: s.editorImage,
                },
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Highlight,
            TextStyle,
            FontSize,
        ],
        content,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: s.editorContent,
            },
            handlePaste: (view, event) => {
                const clipboardEvent = event as ClipboardEvent;
                const items = clipboardEvent.clipboardData?.items;
                if (!items) return false;

                const itemsArray = Array.from(items);
                for (const item of itemsArray) {
                    if (item.type.startsWith('image/')) {
                        event.preventDefault();
                        const file = item.getAsFile();
                        if (file && editor) {
                            handleImageInsert(file, editor);
                        }
                        return true;
                    }
                }
                return false;
            },
            handleDrop: (view, event) => {
                const dragEvent = event as DragEvent;
                const files = dragEvent.dataTransfer?.files;
                if (!files) return false;

                const filesArray = Array.from(files);
                for (const file of filesArray) {
                    if (file.type.startsWith('image/')) {
                        event.preventDefault();
                        if (editor) {
                            handleImageInsert(file, editor);
                        }
                        return true;
                    }
                }
                return false;
            },
        },
    });

    const handleImageInsert = useCallback(async (file: File, editorInstance: any) => {
        if (!editorInstance) return;

        try {
            if (onImageUpload) {
                const imageUrl = await onImageUpload(file);
                editorInstance.chain().focus().setImage({ src: imageUrl }).run();
            } else {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const src = e.target?.result as string;
                    editorInstance.chain().focus().setImage({ src }).run();
                };
                reader.readAsDataURL(file);
            }
        } catch (error) {
            console.error('Ошибка загрузки изображения:', error);
            alert('Не удалось загрузить изображение');
        }
    }, [onImageUpload]);

    const handlePaste = useCallback((event: React.ClipboardEvent) => {
        const items = event.clipboardData?.items;
        if (!items) return;

        const itemsArray = Array.from(items);
        for (const item of itemsArray) {
            if (item.type.startsWith('image/')) {
                event.preventDefault();
                const file = item.getAsFile();
                if (file && editor) {
                    handleImageInsert(file, editor);
                }
                break;
            }
        }
    }, [editor, handleImageInsert]);

    const handleDrop = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        const files = event.dataTransfer?.files;
        if (!files) return;

        const filesArray = Array.from(files);
        for (const file of filesArray) {
            if (file.type.startsWith('image/')) {
                if (editor) {
                    handleImageInsert(file, editor);
                }
                break;
            }
        }
    }, [editor, handleImageInsert]);

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
    };

    if (!editor) {
        return <div className={s.loading}>Загрузка редактора...</div>;
    }

    const insertImage = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true;
        input.onchange = () => {
            const files = input.files;
            if (files && editor) {
                const filesArray = Array.from(files);
                filesArray.forEach(file => {
                    handleImageInsert(file, editor);
                });
            }
        };
        input.click();
    };

    const setFontSize = (size: string) => {
        if (editor) {
            editor.chain().focus().setFontSize(size).run();
        }
    };

    const fontSizes = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'];

    return (
        <div
            className={s.editorContainer}
            onPaste={handlePaste}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
        >
            <div className={s.toolbar}>
                <div className={s.toolbarGroup}>
                    <button
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        className={`${s.toolbarBtn} ${editor.isActive('bold') ? s.active : ''}`}
                        title="Жирный (Ctrl+B)"
                    >
                        <strong>B</strong>
                    </button>
                    <button
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        className={`${s.toolbarBtn} ${editor.isActive('italic') ? s.active : ''}`}
                        title="Курсив (Ctrl+I)"
                    >
                        <em>I</em>
                    </button>
                    <button
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        className={`${s.toolbarBtn} ${editor.isActive('underline') ? s.active : ''}`}
                        title="Подчеркивание (Ctrl+U)"
                    >
                        <u>U</u>
                    </button>
                    <button
                        onClick={() => editor.chain().focus().toggleHighlight().run()}
                        className={`${s.toolbarBtn} ${editor.isActive('highlight') ? s.active : ''}`}
                        title="Выделение цветом"
                    >
                        <span style={{ background: 'yellow', padding: '0 4px' }}>H</span>
                    </button>
                </div>

                <div className={s.divider} />

                <div className={s.toolbarGroup}>
                    <button
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}
                        className={`${s.toolbarBtn} ${editor.isActive({ textAlign: 'left' }) ? s.active : ''}`}
                        title="Выровнять по левому краю"
                    >
                        ⬅️
                    </button>
                    <button
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}
                        className={`${s.toolbarBtn} ${editor.isActive({ textAlign: 'center' }) ? s.active : ''}`}
                        title="Выровнять по центру"
                    >
                        ⬅️➡️
                    </button>
                    <button
                        onClick={() => editor.chain().focus().setTextAlign('right').run()}
                        className={`${s.toolbarBtn} ${editor.isActive({ textAlign: 'right' }) ? s.active : ''}`}
                        title="Выровнять по правому краю"
                    >
                        ➡️
                    </button>
                </div>

                <div className={s.divider} />

                <div className={s.toolbarGroup}>
                    <button
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        className={`${s.toolbarBtn} ${editor.isActive('bulletList') ? s.active : ''}`}
                        title="Маркированный список"
                    >
                        • Список
                    </button>
                    <button
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        className={`${s.toolbarBtn} ${editor.isActive('orderedList') ? s.active : ''}`}
                        title="Нумерованный список"
                    >
                        1. Список
                    </button>
                </div>

                <div className={s.divider} />

                <div className={s.toolbarGroup}>
                    <select
                        onChange={(e) => setFontSize(e.target.value)}
                        className={s.fontSizeSelect}
                        defaultValue="16px"
                        title="Размер шрифта"
                    >
                        {fontSizes.map(size => (
                            <option key={size} value={size}>
                                {size.replace('px', '')}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={s.divider} />

                <div className={s.toolbarGroup}>
                    <button
                        onClick={insertImage}
                        className={s.toolbarBtn}
                        title="Вставить изображение (Ctrl+V или перетащить)"
                    >
                        Фото
                    </button>
                </div>

                <div className={s.divider} />

                <div className={s.toolbarGroup}>
                    <button
                        onClick={() => editor.chain().focus().undo().run()}
                        className={s.toolbarBtn}
                        title="Отменить (Ctrl+Z)"
                    >
                        ↩️
                    </button>
                    <button
                        onClick={() => editor.chain().focus().redo().run()}
                        className={s.toolbarBtn}
                        title="Повторить (Ctrl+Y)"
                    >
                        ↪️
                    </button>
                </div>
            </div>

            <div className={s.editorWrapper}>
                <EditorContent editor={editor} />
            </div>

            <div className={s.footer}>
                <span className={s.hint}>💡 Вставляйте фото: Ctrl+V, перетащите или нажмите "Фото"</span>
            </div>
        </div>
    );
};